import * as React from 'react';
import { SurfaceFocusManager } from './SurfaceFocusManager';

const dataTabIdName = 'data-tabid';

/**
 * Registers the tabbable elements in the given element tree with a focus manager, and sets up
 * a mutation observer listener for when those elements re-render so the focus manager can keep track
 * of the most recent HTMLElements to maintain tab order
 * @param parentElement the parent elemens to register children of
 * @param focusManager the focus manager to register the children to
 * @returns a new JSX Element that has the newly wrapped child elements
 */
export default function registerTabbableChildren(
    parentElement: JSX.Element,
    focusManager: SurfaceFocusManager
): JSX.Element {
    if (!parentElement) {
        return null;
    }

    let childMutationObserver = new MutationObserver(onChildrenChanged(focusManager));
    return React.cloneElement(parentElement, {
        ref: el => {
            let existingRefFunction = (parentElement as React.ClassAttributes<HTMLElement>).ref;
            if (existingRefFunction && typeof existingRefFunction == 'function') {
                existingRefFunction(el);
            }
            if (el) {
                childMutationObserver.observe(el, { childList: true, subtree: true });
                setListenersOnFocusableChildren(el, focusManager);
            } else {
                if (childMutationObserver) {
                    childMutationObserver.disconnect();
                }
            }
        },
    });
}

/**
 * We're setting our mousedown listeners directly on the dom elements for the focusable children, which means that when a sub component
 * inside the FocusableSurfaceComponent rerenders, the mounted dom element gets replaced by a new element. We need to listen for these
 * changes to re-bind our listeners to the new dom elements
 * @param surfaceFocusManager the focus manager to register children for
 * @returns the onMutation callback that registers all added children
 */
function onChildrenChanged(
    surfaceFocusManager: SurfaceFocusManager
): (mutationList: MutationRecord[]) => void {
    return (mutationList: MutationRecord[]) => {
        mutationList.forEach(mutationRecord => {
            var addedNodes = mutationRecord.addedNodes;
            for (let i = 0; i < addedNodes.length; i++) {
                let addedNode: HTMLElement = addedNodes.item(i) as HTMLElement;
                // not all added nodes will be actual elements (like a <text> field isn't an Element, so won't have querySelectorAll on it)
                // those aren't important for us, so we ignore them.
                if (addedNode.querySelectorAll) {
                    setListenersOnFocusableChildren(addedNode, surfaceFocusManager);
                }
            }
        });
    };
}

function setListenersOnFocusableChildren(
    node: HTMLElement,
    surfaceFocusManager: SurfaceFocusManager
) {
    if (node.getAttribute(dataTabIdName)) {
        registerElement(node, surfaceFocusManager);
    } else {
        let tabbableChildren = node.querySelectorAll('[' + dataTabIdName + ']');
        for (let i = 0; i < tabbableChildren.length; i++) {
            let item = tabbableChildren.item(i) as HTMLElement;
            registerElement(item, surfaceFocusManager);
        }
    }
}

function registerElement(element: HTMLElement, surfaceFocusManager: SurfaceFocusManager) {
    surfaceFocusManager.registerTabbableElement(element, element.getAttribute(dataTabIdName));
    let existingOnMouseDown = element.onmousedown;
    let newOnMouseDown = onItemMouseDown(surfaceFocusManager, existingOnMouseDown);
    if (
        (newOnMouseDown && !existingOnMouseDown) ||
        (existingOnMouseDown &&
            newOnMouseDown &&
            existingOnMouseDown.toString() != newOnMouseDown.toString())
    ) {
        element.onmousedown = newOnMouseDown;
    }
}

function onItemMouseDown(
    focusManager: SurfaceFocusManager,
    existingOnMouseDown: (ev: MouseEvent) => any
) {
    return (evt: MouseEvent) => {
        focusManager.itemClicked(evt.currentTarget as HTMLElement);
        if (existingOnMouseDown && typeof existingOnMouseDown === 'function') {
            existingOnMouseDown(evt);
        }
    };
}
